import React from 'react';
import i18n from '@/i18n';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useBusinessAuth } from '@/contexts/BusinessAuthContext';
import LoadingScreen from '@/components/LoadingScreen';
import { Panel, Empty, Button } from '@/components/bz';

const LABELS = {
  procurement: i18n.t('c.business.RequireCapability.procurement_co_pilot'),
  sales: i18n.t('c.business.RequireCapability.sales_co_pilot'),
  god_mode: i18n.t('c.business.RequireCapability.autonomous_programs'),
};

/**
 * Route guard for a Co-Pilot section.
 *
 * Renders an explanatory screen rather than bouncing the user somewhere
 * else — a silent redirect makes a permissions problem look like a broken
 * link, and the person then asks support instead of their administrator.
 *
 * This is presentation only. The API enforces the same rule independently,
 * so editing it in devtools gains nothing.
 */
export default function RequireCapability({ capability, children }) {
  const { t } = useTranslation();
  const { loading, hasCapability, capabilityReason } = useBusinessAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (hasCapability(capability)) return children;

  const label = LABELS[capability] || 'This module';
  const reason = capabilityReason(capability);

  return (
    <div className="max-w-md mx-auto py-10" data-testid={`capability-denied-${capability}`}>
      <Panel>
        <Empty
          title={`${label} ${t('c.business.RequireCapability.isn_t_enabled_for_you')}`}
          body={<>{t('c.business.RequireCapability.your_workspace_administrator_controls_who_can')}{reason && <span className="block mt-2 bz-mono text-[11.5px] text-bz-ink-3">{reason}</span>}</>}
          action={<Button variant="primary" icon={ArrowLeft} to="/business/dashboard" state={{ from: location }}>{t('c.business.RequireCapability.back_to_dashboard')}</Button>}
        />
      </Panel>
    </div>
  );
}
